// Los juegos de un torneo INTERNO, en la forma que entiende el flyer del rol.
//
// `buildRolFlyer` nacio para el rol de la organizadora y consume lo que devuelve el RPC
// `get_rol_jornada`: local/visita, campo, categoria. Un juego interno vive en la tabla
// `juegos`, que tiene otra forma -- y sobre todo, otra IDEA.
//
// LA DIFERENCIA QUE IMPORTA: `juegos` guarda `puntos_favor` y `puntos_contra`, o sea que
// esta escrita desde el punto de vista DEL CLUB. Desde 2026-09-04 tambien guarda
// `condicion` (local | visita): si el grupo jugo de visita, el rival va a la izquierda y
// el club a la derecha, con el marcador volteado para que cada lado tenga sus puntos. Sin
// el dato (juegos viejos), el club va a la izquierda, como siempre.

import { fixMojibake } from './utils'

// La columna del flyer. `cancha` es lo que el usuario escribe ("Cancha 1"); si no la
// puso, sirve el numero de campo. Devolver null deja que el flyer ponga "Sin campo",
// que es su propia palabra para lo mismo.
// Manda el NUMERO de campo, no el texto de la cancha. El formulario del juego lo dice
// con todas sus letras bajo "Numero de campo" ("nos ayuda a agrupar el rol de juegos por
// campo") y la tarjeta pinta ese numero. La cancha es texto libre: en LTL KIDS (PROD)
// vale "1" en los cuatro juegos del dia, asi que tomarla primero metia todos los partidos
// en un solo campo y el rol se negaba a salir (el dueno, 2026-09-08). La cancha queda de
// respaldo para quien solo la captura.
export const campoDe = (juego) => {
  const numero = juego.campo_numero
  if (numero !== null && numero !== undefined && String(numero).trim() !== '') {
    return `Campo ${numero}`
  }
  const cancha = (juego.cancha || '').trim()
  if (cancha) return fixMojibake(cancha)
  return null
}

const grupoDe = (juego) =>
  juego.grupo?.nombre_del_grupo || juego.grupos?.nombre_del_grupo || null

const rivalDe = (juego) =>
  juego.equipo?.nombre || juego.equipo_nombre || null

// Solo 'visita' voltea. Cualquier otra cosa (null, '', 'local', basura) deja al club a
// la izquierda: es el comportamiento de siempre y el que no puede mentir.
export const juegaDeVisita = (juego) => juego?.condicion === 'visita'

export const filaDeRolInterno = (juego, { clubLogoUrl = null } = {}) => {
  if (!juego) return null
  const grupo = grupoDe(juego)
  const club = { nombre: grupo ? fixMojibake(grupo) : 'Mi equipo', logo: clubLogoUrl, puntos: juego.puntos_favor ?? null }
  const rival = { nombre: rivalDe(juego) ? fixMojibake(rivalDe(juego)) : 'Por definir', logo: juego.equipo?.logo_url || null, puntos: juego.puntos_contra ?? null }
  const [izq, der] = juegaDeVisita(juego) ? [rival, club] : [club, rival]
  return {
    fecha: juego.fecha || null,
    hora: juego.hora || null,
    sede: juego.ubicacion ? fixMojibake(juego.ubicacion) : null,
    campo: campoDe(juego),
    local_club: izq.nombre,
    local_logo: izq.logo,
    visita_club: der.nombre,
    visita_logo: der.logo,
    // El color del flyer se agrupa por "categoria". Aqui la unidad real es el GRUPO:
    // es lo que distingue un partido de otro dentro del mismo club.
    categoria_nombre: grupo ? fixMojibake(grupo) : 'Sin grupo',
    // Se conserva el 0: un 0-0 es un marcador, no un partido sin jugar. `??` y no `||`
    // justamente por eso.
    marcador_local: izq.puntos,
    marcador_visita: der.puntos,
    // El flyer tacha el partido cuando el estatus es 'finalizado'; en esta app se llama
    // 'terminado'.
    estatus: juego.estatus === 'terminado' ? 'finalizado' : juego.estatus || null,
  }
}

export const filasDeRolInterno = (juegos, opciones = {}) =>
  (juegos || []).map((j) => filaDeRolInterno(j, opciones)).filter(Boolean)

// El renglon chico que va bajo el nombre del torneo en el flyer.
//
// En un torneo de la red ahi va la jornada, que la organizadora siempre pone. En uno
// interno la jornada es OPCIONAL, y repetir el nombre del torneo -- que ya esta arriba en
// grande -- solo lo dice dos veces. Asi que: si el dia entero es de una misma jornada, se
// nombra; si no, se calla. La fecha no hace falta, el flyer ya la imprime en su banda.
export const tituloDelRolInterno = (juegos) => {
  const jornadas = new Set(
    (juegos || [])
      .map((j) => (j?.jornada === null || j?.jornada === undefined ? '' : String(j.jornada).trim()))
      .filter(Boolean)
  )
  if (jornadas.size !== 1) return ''
  const unica = [...jornadas][0]
  // Si ya viene escrita como texto ("Jornada 3", "J3"), no se le antepone nada.
  return /^\d+$/.test(unica) ? `Jornada ${unica}` : unica
}
